import {useState, useEffect, useCallback} from 'react';

// Sub-pixel scroll positions can leave scrollLeft fractionally short of the
// maximum, so "within 1px of the end" counts as at-the-end. The same rounding
// makes a table that visually fits report an overflow of a pixel, so the
// tolerance also decides whether there is anything to scroll at all.
const END_TOLERANCE_PX = 1

// How long after the last scroll event the user counts as done scrolling.
const SCROLL_IDLE_DELAY_MS = 150;

const scrollEdges = (el) => {
  const maxScroll = el.scrollWidth - el.clientWidth;
  return {
    isScrollable: maxScroll > END_TOLERANCE_PX,
    isAtStart: el.scrollLeft <= 0,
    isAtEnd: maxScroll <= 0 || el.scrollLeft >= maxScroll - END_TOLERANCE_PX,
  };
};

// Tracks horizontal scroll affordances (edge positions, scrollability,
// scroll-in-progress) for the element behind scrollRef.
//
// Both effects capture scrollRef.current once on mount and never re-attach,
// so the ref must point to a node that exists on first render and stays the
// same for the component's lifetime
const useScrollState = (scrollRef) => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const checkScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const edges = scrollEdges(el);
    setIsScrollable(edges.isScrollable);
    // Refresh the position states too: the offset can change without a scroll
    // event (e.g. the element stops being a scroll container on collapse).
    setIsAtStart(edges.isAtStart);
    setIsAtEnd(edges.isAtEnd);
  }, [scrollRef]);

  // Scroll listener: tracks the edge states and flags scrolling as
  // in-progress until the events go idle.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let idleTimer = null;

    const handleScroll = () => {
      const edges = scrollEdges(el);
      setIsAtStart(edges.isAtStart);
      setIsAtEnd(edges.isAtEnd);

      setIsScrolling(true);
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setIsScrolling(false), SCROLL_IDLE_DELAY_MS);
    };

    el.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [scrollRef]);

  // ResizeObserver: re-derives scrollability when the element's size changes.
  useEffect(() => {
    checkScrollable();
    const observer = new ResizeObserver(checkScrollable);
    if (scrollRef.current) observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [checkScrollable]);

  return { isAtStart, isAtEnd, isScrolling, isScrollable, checkScrollable };
};

export default useScrollState;
